import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
// Adjust if your store file is "@/store" (index.ts) instead of "@/store/store".
import type { RootState } from "@/store";

export type OnchainDepositStatus = "idle" | "processing" | "settled";

export interface OnchainDepositState {
  status: OnchainDepositStatus;
  // The Bitcoin address we watch for this on-chain deposit (e.g. bcrt1q...).
  // This is the key that links the webhook back to this browser.
  address: string | null;
  // The Paidly order id (e.g. abbea836...). Saved ONLY so we can record the
  // payment when the deposit settles. We do NOT watch by this.
  orderId: string | null;
  txHash: string | null;
  confirmations: number | null;
  // Epoch milliseconds when "processing" began (informational; not used by the
  // 15-minute timer).
  processingStartedAt: number | null;
}

const initialState: OnchainDepositState = {
  status: "idle",
  address: null,
  orderId: null,
  txHash: null,
  confirmations: null,
  processingStartedAt: null,
};

const onchainDepositSlice = createSlice({
  name: "onchainDeposit",
  initialState,
  reducers: {
    setOnchainDepositProcessing: (
      state,
      action: PayloadAction<{
        address: string;
        orderId: string;
        txHash?: string;
        confirmations?: number;
      }>,
    ) => {
      state.status = "processing";
      state.address = action.payload.address;
      state.orderId = action.payload.orderId;
      state.txHash = action.payload.txHash ?? state.txHash;
      state.confirmations = action.payload.confirmations ?? state.confirmations;
      // Stamp the start time only ONCE; a second "processing" webhook (more
      // confirmations) must not reset it.
      if (state.processingStartedAt === null) {
        state.processingStartedAt = Date.now();
      }
    },
    updateOnchainConfirmations: (
      state,
      action: PayloadAction<{ confirmations: number }>,
    ) => {
      state.confirmations = action.payload.confirmations;
    },
    setOnchainDepositSettled: (state) => {
      state.status = "settled";
    },
    resetOnchainDeposit: () => initialState,
  },
});

export const {
  setOnchainDepositProcessing,
  updateOnchainConfirmations,
  setOnchainDepositSettled,
  resetOnchainDeposit,
} = onchainDepositSlice.actions;

export const getOnchainDeposit = (state: RootState) => state.onchainDeposit;
export const getIsOnchainProcessing = (state: RootState) =>
  state.onchainDeposit.status === "processing";

export default onchainDepositSlice.reducer;
